import Squire from 'squire-rte';
import type { Component, Ref } from 'vue';

export type EditorControl = {
  tag: string;
  disableIfImageSelected: boolean;
} & (
  | {
      type: 'button' | 'toggle';
      icon: Component;
      action: (editor: Squire, tag: string, selectedImage: Ref<HTMLImageElement | null>) => void;
    }
  | {
      type: 'select';
      values: string[];
      default: string;
      getCurrent: (editor: Squire) => string;
      action: (selectedValue: string, editor: Squire, tag: string) => void;
    }
  | {
      type: 'add-link' | 'line-spacing' | 'color-picker';
      icon: Component;
      action: (value: string, editor: Squire) => void;
    }
  | {
      type: 'add-table';
      icon: Component;
      action: (width: number, height: number, editor: Squire) => HTMLTableElement;
    }
  | {
      type: 'add-image';
      icon: Component;
      action: (files: FileList, editor: Squire) => void;
    }
);

export const INDENT_STEP = 20;
export const MAX_INDENT = 100;
export const FALLBACK_FONT = 'Basier Circle';

function isElement(node: Node): node is Element {
  return (node as Element).tagName !== undefined;
}

const BLOCK_TAGS = ['DIV', 'P', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
function isBlockElement(node: Node): node is HTMLElement {
  return isElement(node) && BLOCK_TAGS.includes(node.tagName);
}

function indentBlockElement(blockElement: HTMLElement) {
  const currentIndent = parseInt(blockElement.style.marginLeft) || 0;
  blockElement.style.marginLeft = `${Math.min(MAX_INDENT, currentIndent + INDENT_STEP)}px`;
}

function outdentBlockElement(blockElement: HTMLElement) {
  const currentIndent = parseInt(blockElement.style.marginLeft) || 0;
  blockElement.style.marginLeft = `${Math.max(0, currentIndent - INDENT_STEP)}px`;
}

function findBlockElementsInRange(range: Range): HTMLElement[] {
  const blockElements = new Set<HTMLElement>();

  const startElement = isElement(range.startContainer)
    ? range.startContainer
    : range.startContainer.parentElement;
  const endElement = isElement(range.endContainer)
    ? range.endContainer
    : range.endContainer.parentElement;

  if (!startElement || !endElement) return [];

  const walker = document.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_ELEMENT,
    (node) => {
      if (range.intersectsNode(node)) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_SKIP;
    }
  );

  walker.currentNode = startElement;
  let currentNode: Node | null = startElement;

  while (currentNode) {
    if (isBlockElement(currentNode)) {
      blockElements.add(currentNode);
    }
    if (currentNode === endElement || currentNode.contains(endElement)) {
      break;
    }
    currentNode = walker.nextNode();
  }

  return Array.from(blockElements);
}

export function changeIndent(editor: Squire, indent: 'in' | 'out') {
  if (indent === 'in') {
    editor.increaseListLevel();
  } else {
    editor.decreaseListLevel();
  }

  const range = editor.getSelection();
  if (!range) return;

  const dentFunction = indent == 'in' ? indentBlockElement : outdentBlockElement;
  findBlockElementsInRange(range).forEach(dentFunction);
}

export function copyImageToClip(image: HTMLImageElement) {
  fetch(image.src)
    .then((res) => res.blob())
    .then((blob) => {
      const data = [new ClipboardItem({ [blob.type]: blob })];
      navigator.clipboard.write(data);
    });
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

export function after(fn: () => void, ms: number) {
  setTimeout(fn, ms);
}

// table functions
export function insertRow(table: HTMLTableElement, before: number, colCount: number) {
  const trs = Array.from(table.querySelectorAll('tr'));
  const newRow = document.createElement('tr');
  for (let c = 0; c < colCount; c++) {
    const td = document.createElement('td');
    td.setAttribute(
      'style',
      'border: 1px solid var(--nxt-border); padding: 6px 10px; min-width: 40px; vertical-align: top;'
    );
    td.innerHTML = '<br>';
    newRow.appendChild(td);
  }
  if (before >= trs.length) {
    trs.at(-1)?.parentElement?.appendChild(newRow);
  } else {
    trs[before].parentElement?.insertBefore(newRow, trs[before]);
  }
}
export function insertCol(table: HTMLTableElement, before: number) {
  table.querySelectorAll('tr').forEach((tr) => {
    const newCol = document.createElement('td');
    newCol.setAttribute(
      'style',
      'border: 1px solid var(--nxt-border); padding: 6px 10px; min-width: 40px; vertical-align: top;'
    );
    newCol.innerHTML = '<br>';
    const tds = Array.from(tr.querySelectorAll('td'));
    if (before >= tds.length) {
      tds.at(-1)?.parentElement?.appendChild(newCol);
    } else {
      tds[before].parentElement?.insertBefore(newCol, tds[before]);
    }
  });
}
export function deleteRow(table: HTMLTableElement, index: number) {
  const trs = Array.from(table.querySelectorAll('tr'));
  trs[index].remove();
}
export function deleteCol(table: HTMLTableElement, index: number) {
  table.querySelectorAll('tr').forEach((tr) => {
    const tds = Array.from(tr.querySelectorAll('td'));
    if (index >= tds.length) return;
    tds[index].remove();
  });
}
